import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiFetch } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, LogOut, Plus, Trash2, Upload, ImageOff, ExternalLink } from "lucide-react";

function Field({ label, value, onChange, textarea, placeholder }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-stone-500">{label}</Label>
      {textarea ? (
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1.5 bg-white" rows={3} />
      ) : (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1.5 bg-white" />
      )}
    </div>
  );
}

function ImageField({ label, value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const data = await apiFetch("/admin/upload", { method: "POST", body: fd, isForm: true });
      onChange(data.url);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(err.message || "Error al subir imagen");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-stone-500">{label}</Label>
      <div className="mt-1.5 flex items-center gap-4">
        <div className="h-20 w-20 shrink-0 overflow-hidden border border-stone-200 bg-stone-100 flex items-center justify-center">
          {value ? <img src={value} alt="" className="h-full w-full object-cover" /> : <ImageOff size={20} className="text-stone-400" />}
        </div>
        <div className="flex-1 space-y-2">
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL de imagen" className="bg-white text-xs" />
          <div className="flex gap-2">
            <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
            <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading}>
              <Upload size={14} className="mr-1.5" /> {uploading ? "Subiendo..." : "Subir imagen"}
            </Button>
            {value && (
              <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} className="text-stone-500">
                <Trash2 size={14} className="mr-1.5" /> Quitar
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiFetch("/content").then((data) => setC(data)).catch(() => toast.error("No se pudo cargar el contenido"));
  }, []);

  const set = (key, val) => setC((prev) => ({ ...prev, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch("/content", { method: "PUT", body: c });
      toast.success("Cambios guardados");
    } catch (err) {
      toast.error(err.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  // ---- signature helpers ----
  const updateSig = (i, key, val) => set("signature", c.signature.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)));
  const addSig = () => set("signature", [...c.signature, { id: crypto.randomUUID(), name: "Nuevo plato", description: "", price: "$0.00", image: "" }]);
  const delSig = (i) => set("signature", c.signature.filter((_, idx) => idx !== i));

  // ---- chapters ----
  const updateCh = (i, key, val) => set("chapters", c.chapters.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)));
  const addCh = () => set("chapters", [...c.chapters, { id: crypto.randomUUID(), number: "0" + (c.chapters.length + 1), title: "", text: "" }]);
  const delCh = (i) => set("chapters", c.chapters.filter((_, idx) => idx !== i));

  // ---- menu ----
  const updateCat = (ci, val) => set("menu", c.menu.map((cat, idx) => (idx === ci ? { ...cat, name: val } : cat)));
  const addCat = () => set("menu", [...c.menu, { id: crypto.randomUUID(), name: "Nueva categoría", items: [] }]);
  const delCat = (ci) => set("menu", c.menu.filter((_, idx) => idx !== ci));
  const updateItem = (ci, ii, key, val) =>
    set("menu", c.menu.map((cat, idx) => idx !== ci ? cat : { ...cat, items: cat.items.map((it, j) => (j === ii ? { ...it, [key]: val } : it)) }));
  const addItem = (ci) =>
    set("menu", c.menu.map((cat, idx) => idx !== ci ? cat : { ...cat, items: [...cat.items, { id: crypto.randomUUID(), name: "", description: "", price: "", image: "" }] }));
  const delItem = (ci, ii) =>
    set("menu", c.menu.map((cat, idx) => idx !== ci ? cat : { ...cat, items: cat.items.filter((_, j) => j !== ii) }));

  // ---- reviews ----
  const updateReview = (i, key, val) => set("reviews", c.reviews.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const addReview = () => set("reviews", [...(c.reviews || []), { id: crypto.randomUUID(), name: "", rating: 5, text: "", date: "Google" }]);
  const delReview = (i) => set("reviews", c.reviews.filter((_, idx) => idx !== i));

  // ---- gallery ----
  const updateGallery = (i, key, val) => set("gallery", (c.gallery || []).map((g, idx) => (idx === i ? { ...g, [key]: val } : g)));
  const addGallery = () => set("gallery", [...(c.gallery || []), { id: crypto.randomUUID(), image: "", caption: "" }]);
  const delGallery = (i) => set("gallery", c.gallery.filter((_, idx) => idx !== i));

  // ---- reservations ----
  const [reservations, setReservations] = useState([]);
  const loadReservations = () => apiFetch("/reservations").then(setReservations).catch(() => {});
  useEffect(() => { loadReservations(); }, []);
  const setResStatus = async (rid, status) => {
    await apiFetch(`/reservations/${rid}`, { method: "PATCH", body: { status } }).catch((e) => toast.error(e.message));
    loadReservations();
  };
  const delRes = async (rid) => {
    await apiFetch(`/reservations/${rid}`, { method: "DELETE" }).catch((e) => toast.error(e.message));
    loadReservations();
  };

  if (!c) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f6f0]">
        <div className="h-10 w-10 border-2 border-[#da251d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f1ea]">
      <header className="sticky top-0 z-30 bg-white border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={c.logo} alt="" className="h-9 w-auto object-contain" />
            <span className="font-display text-lg font-semibold hidden sm:block">Panel de administración</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noreferrer" className="text-sm text-stone-500 hover:text-[#da251d] inline-flex items-center gap-1 px-3">
              Ver sitio <ExternalLink size={14} />
            </a>
            <Button onClick={save} disabled={saving} data-testid="save-content-btn" className="bg-[#da251d] hover:bg-[#b91c1c] text-white rounded-full">
              <Save size={16} className="mr-1.5" /> {saving ? "Guardando..." : "Guardar"}
            </Button>
            <Button onClick={doLogout} variant="ghost" size="icon" data-testid="logout-btn" title="Salir">
              <LogOut size={18} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        <Tabs defaultValue="general">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
            <TabsTrigger value="historia" data-testid="tab-historia">Historia</TabsTrigger>
            <TabsTrigger value="platos" data-testid="tab-platos">Platos destacados</TabsTrigger>
            <TabsTrigger value="galeria" data-testid="tab-galeria">Galería</TabsTrigger>
            <TabsTrigger value="menu" data-testid="tab-menu">Menú</TabsTrigger>
            <TabsTrigger value="resenas" data-testid="tab-resenas">Reseñas</TabsTrigger>
            <TabsTrigger value="reservas" data-testid="tab-reservas">Reservas</TabsTrigger>
            <TabsTrigger value="contacto" data-testid="tab-contacto">Contacto</TabsTrigger>
          </TabsList>

          {/* GENERAL */}
          <TabsContent value="general" className="space-y-6 bg-white p-6 border border-stone-200">
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Nombre del negocio" value={c.brand_name} onChange={(v) => set("brand_name", v)} />
              <Field label="Eslogan" value={c.tagline} onChange={(v) => set("tagline", v)} />
            </div>
            <ImageField label="Logo (aparece en el hero)" value={c.logo} onChange={(v) => set("logo", v)} />
            <div className="grid md:grid-cols-3 gap-5">
              <Field label="Título hero — línea 1" value={c.hero_line1} onChange={(v) => set("hero_line1", v)} />
              <Field label="Título hero — línea 2" value={c.hero_line2} onChange={(v) => set("hero_line2", v)} />
              <Field label="Título hero — línea 3" value={c.hero_line3} onChange={(v) => set("hero_line3", v)} />
            </div>
            <Field label="Subtítulo hero" value={c.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} textarea />
            <ImageField label="Imagen de fondo del hero" value={c.hero_background} onChange={(v) => set("hero_background", v)} />
            <Field label="Enlace de DoorDash (Ordenar ahora)" value={c.doordash_url} onChange={(v) => set("doordash_url", v)} />
          </TabsContent>

          {/* HISTORIA */}
          <TabsContent value="historia" className="space-y-6 bg-white p-6 border border-stone-200">
            <Field label="Título de la sección" value={c.about_heading} onChange={(v) => set("about_heading", v)} />
            <Field label="Introducción" value={c.about_intro} onChange={(v) => set("about_intro", v)} textarea />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Capítulos</h3>
                <Button size="sm" variant="outline" onClick={addCh}><Plus size={14} className="mr-1" /> Añadir</Button>
              </div>
              {c.chapters.map((ch, i) => (
                <div key={ch.id} className="border border-stone-200 p-4 grid md:grid-cols-[80px_1fr] gap-4 relative">
                  <Field label="N°" value={ch.number} onChange={(v) => updateCh(i, "number", v)} />
                  <div className="space-y-3">
                    <Field label="Título" value={ch.title} onChange={(v) => updateCh(i, "title", v)} />
                    <Field label="Texto" value={ch.text} onChange={(v) => updateCh(i, "text", v)} textarea />
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => delCh(i)} className="absolute top-2 right-2 text-stone-400 hover:text-[#da251d]"><Trash2 size={16} /></Button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* PLATOS */}
          <TabsContent value="platos" className="space-y-6 bg-white p-6 border border-stone-200">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Platos destacados</h3>
              <Button size="sm" variant="outline" onClick={addSig} data-testid="add-signature-btn"><Plus size={14} className="mr-1" /> Añadir plato</Button>
            </div>
            {c.signature.map((s, i) => (
              <div key={s.id} className="border border-stone-200 p-4 space-y-3 relative">
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Nombre" value={s.name} onChange={(v) => updateSig(i, "name", v)} />
                  <Field label="Precio" value={s.price} onChange={(v) => updateSig(i, "price", v)} />
                </div>
                <Field label="Descripción" value={s.description} onChange={(v) => updateSig(i, "description", v)} textarea />
                <ImageField label="Imagen" value={s.image} onChange={(v) => updateSig(i, "image", v)} />
                <Button size="icon" variant="ghost" onClick={() => delSig(i)} className="absolute top-2 right-2 text-stone-400 hover:text-[#da251d]"><Trash2 size={16} /></Button>
              </div>
            ))}
          </TabsContent>

          {/* GALERIA */}
          <TabsContent value="galeria" className="space-y-6 bg-white p-6 border border-stone-200">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Galería de fotos</h3>
              <Button size="sm" variant="outline" onClick={addGallery} data-testid="add-gallery-btn"><Plus size={14} className="mr-1" /> Añadir foto</Button>
            </div>
            {(c.gallery || []).map((g, i) => (
              <div key={g.id} className="border border-stone-200 p-4 space-y-3 relative">
                <ImageField label={`Foto ${i + 1}`} value={g.image} onChange={(v) => updateGallery(i, "image", v)} />
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Descripción (opcional)" value={g.caption} onChange={(v) => updateGallery(i, "caption", v)} />
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-stone-500">Categoría</Label>
                    <select
                      value={g.category || "platos"}
                      onChange={(e) => updateGallery(i, "category", e.target.value)}
                      className="mt-1.5 w-full h-10 rounded-md border border-input bg-white px-3 text-sm"
                    >
                      <option value="platos">Platos</option>
                      <option value="mariscos">Mariscos</option>
                      <option value="postres">Postres</option>
                      <option value="bebidas">Bebidas</option>
                    </select>
                  </div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => delGallery(i)} className="absolute top-2 right-2 text-stone-400 hover:text-[#da251d]"><Trash2 size={16} /></Button>
              </div>
            ))}
          </TabsContent>

          {/* MENU */}
          <TabsContent value="menu" className="space-y-6 bg-white p-6 border border-stone-200">
            <Field label="Especial de fin de semana (banner rojo del menú)" value={c.weekend_special} onChange={(v) => set("weekend_special", v)} placeholder="Todos los fines de semana: Pollo a la Brasa" />
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Categorías del menú</h3>
              <Button size="sm" variant="outline" onClick={addCat} data-testid="add-category-btn"><Plus size={14} className="mr-1" /> Añadir categoría</Button>
            </div>
            {c.menu.map((cat, ci) => (
              <div key={cat.id} className="border border-stone-200 p-4 space-y-4">
                <div className="flex items-end gap-3">
                  <div className="flex-1"><Field label="Nombre de categoría" value={cat.name} onChange={(v) => updateCat(ci, v)} /></div>
                  <Button size="sm" variant="ghost" onClick={() => delCat(ci)} className="text-stone-400 hover:text-[#da251d]"><Trash2 size={16} className="mr-1" /> Eliminar categoría</Button>
                </div>
                <div className="space-y-3 pl-1">
                  {cat.items.map((it, ii) => (
                    <div key={it.id} className="grid md:grid-cols-[1fr_1fr_100px_auto] gap-3 items-end border-t border-dashed border-stone-200 pt-3">
                      <Field label="Nombre" value={it.name} onChange={(v) => updateItem(ci, ii, "name", v)} />
                      <Field label="Descripción" value={it.description} onChange={(v) => updateItem(ci, ii, "description", v)} />
                      <Field label="Precio" value={it.price} onChange={(v) => updateItem(ci, ii, "price", v)} />
                      <Button size="icon" variant="ghost" onClick={() => delItem(ci, ii)} className="text-stone-400 hover:text-[#da251d] mb-1"><Trash2 size={16} /></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => addItem(ci)}><Plus size={14} className="mr-1" /> Añadir plato</Button>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* RESEÑAS */}
          <TabsContent value="resenas" className="space-y-6 bg-white p-6 border border-stone-200">
            <div className="grid md:grid-cols-3 gap-5">
              <Field label="Calificación promedio" value={c.rating_average} onChange={(v) => set("rating_average", v)} />
              <Field label="N° de reseñas" value={c.rating_count} onChange={(v) => set("rating_count", v)} />
              <Field label="Enlace a Google Reviews" value={c.google_reviews_url} onChange={(v) => set("google_reviews_url", v)} />
            </div>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Reseñas</h3>
              <Button size="sm" variant="outline" onClick={addReview} data-testid="add-review-btn"><Plus size={14} className="mr-1" /> Añadir reseña</Button>
            </div>
            {(c.reviews || []).map((r, i) => (
              <div key={r.id} className="border border-stone-200 p-4 space-y-3 relative">
                <div className="grid md:grid-cols-3 gap-4">
                  <Field label="Nombre" value={r.name} onChange={(v) => updateReview(i, "name", v)} />
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-stone-500">Estrellas (1-5)</Label>
                    <Input type="number" min="1" max="5" value={r.rating} onChange={(e) => updateReview(i, "rating", Number(e.target.value))} className="mt-1.5 bg-white" />
                  </div>
                  <Field label="Fuente" value={r.date} onChange={(v) => updateReview(i, "date", v)} />
                </div>
                <Field label="Texto" value={r.text} onChange={(v) => updateReview(i, "text", v)} textarea />
                <Button size="icon" variant="ghost" onClick={() => delReview(i)} className="absolute top-2 right-2 text-stone-400 hover:text-[#da251d]"><Trash2 size={16} /></Button>
              </div>
            ))}
            <p className="text-xs text-stone-400">Recuerda pulsar "Guardar" para publicar los cambios de reseñas.</p>
          </TabsContent>

          {/* RESERVAS */}
          <TabsContent value="reservas" className="space-y-4 bg-white p-6 border border-stone-200">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Reservas recibidas ({reservations.length})</h3>
              <Button size="sm" variant="outline" onClick={loadReservations} data-testid="refresh-reservations-btn">Actualizar</Button>
            </div>
            {reservations.length === 0 && <p className="text-stone-400 text-sm py-6 text-center">Aún no hay reservas.</p>}
            {reservations.map((r) => (
              <div key={r.id} className="border border-stone-200 p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`reservation-row-${r.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{r.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "confirmed" ? "bg-green-100 text-green-700" : r.status === "cancelled" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                      {r.status === "confirmed" ? "Confirmada" : r.status === "cancelled" ? "Cancelada" : "Pendiente"}
                    </span>
                  </div>
                  <p className="text-sm text-[#57534e] mt-1">
                    {r.date} · {r.time} · {r.guests} personas · {r.phone}{r.email ? ` · ${r.email}` : ""}
                  </p>
                  {r.notes && <p className="text-sm text-stone-500 mt-1 italic">"{r.notes}"</p>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setResStatus(r.id, "confirmed")} className="text-green-700">Confirmar</Button>
                  <Button size="sm" variant="outline" onClick={() => setResStatus(r.id, "cancelled")} className="text-red-600">Cancelar</Button>
                  <Button size="icon" variant="ghost" onClick={() => delRes(r.id)} className="text-stone-400 hover:text-[#da251d]"><Trash2 size={16} /></Button>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* CONTACTO */}
          <TabsContent value="contacto" className="space-y-5 bg-white p-6 border border-stone-200">
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Dirección" value={c.address} onChange={(v) => set("address", v)} />
              <Field label="Teléfono" value={c.phone} onChange={(v) => set("phone", v)} />
              <Field label="WhatsApp (con código país)" value={c.whatsapp} onChange={(v) => set("whatsapp", v)} />
              <Field label="Correo" value={c.email} onChange={(v) => set("email", v)} />
            </div>
            <Field label="Horario" value={c.hours} onChange={(v) => set("hours", v)} />
            <Field label="URL del mapa (Google Maps embed)" value={c.map_embed} onChange={(v) => set("map_embed", v)} />
            <div className="pt-4 border-t border-stone-200">
              <h3 className="font-semibold mb-3">Sección Eventos / Catering</h3>
              <div className="space-y-4">
                <Field label="Título de Eventos" value={c.events_heading} onChange={(v) => set("events_heading", v)} />
                <Field label="Texto de Eventos" value={c.events_text} onChange={(v) => set("events_text", v)} textarea />
                <ImageField label="Imagen de Eventos" value={c.events_image} onChange={(v) => set("events_image", v)} />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
